<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import {
  Bot,
  Check,
  ClipboardCheck,
  Save,
  Send,
  ShieldCheck,
  UserRoundCheck,
  X,
} from "lucide-vue-next";
import { v3 } from "@/services/v3";

const staff = ref<any[]>([]);
const approvals = ref<any[]>([]);
const projects = ref<any[]>([]);
const selectedProjectId = ref<number | null>(null);
const suggestions = ref<Record<number, any[]>>({});
const selectedAssignees = ref<Record<number, number | null>>({});
const reviewNote = ref("");
const analysisInput = ref("");
const analysis = ref<any>(null);
const loading = ref(false);

const selectedProject = computed(() =>
  projects.value.find((project) => project.id === selectedProjectId.value) || projects.value[0] || null,
);

async function load() {
  const [staffResult, approvalResult, projectResult] = await Promise.all([
    v3.staff(),
    v3.approvals(),
    v3.adminProjects(),
  ]);
  staff.value = staffResult.staff.map((member: any) => ({
    ...member,
    capabilityText: member.capabilities.map((item: any) => `${item.capability}:${item.level}`).join(", "),
  }));
  approvals.value = approvalResult.approvals;
  projects.value = projectResult.projects;
  if (!selectedProjectId.value && projects.value[0]) selectedProjectId.value = projects.value[0].id;
}

async function run(action: () => Promise<unknown>) {
  loading.value = true;
  try {
    await action();
    await load();
  } finally {
    loading.value = false;
  }
}

async function save(member: any) {
  const capabilities = member.capabilityText
    .split(",")
    .map((value: string) => {
      const [name, level] = value.trim().split(":");
      return { capability: name, level: Number(level) || 3 };
    })
    .filter((item: any) => item.capability);
  await run(() => v3.saveCapabilities(member.id, {
    capabilities,
    capacity: member.availability.capacity,
    currentLoad: member.availability.current_load,
    availableFrom: member.availability.available_from,
  }));
}

async function loadSuggestions(taskId: number) {
  suggestions.value[taskId] = (await v3.suggestions(taskId)).suggestions;
  selectedAssignees.value[taskId] ||= suggestions.value[taskId][0]?.id || null;
}

async function assignTask(task: any) {
  const assigneeId = selectedAssignees.value[task.id];
  if (!assigneeId) return;
  await run(() => v3.assign(task.id, assigneeId, `管理员根据能力和负载分派：${task.title}`));
}

async function triage(action: "assign" | "client_review" | "return") {
  if (!selectedProject.value) return;
  await run(() => v3.triage(selectedProject.value.id, { action, note: reviewNote.value }));
  reviewNote.value = "";
}

async function analyze() {
  if (!analysisInput.value.trim()) return;
  loading.value = true;
  try {
    analysis.value = (await v3.aiAnalyze(analysisInput.value)).analysis;
  } finally {
    loading.value = false;
  }
}

onMounted(() => void load());
</script>

<template>
  <div class="admin">
    <header><h1>管理员调度与审核</h1><p>先分析客户需求，再确认、分派或送客户复核；员工交付必须由管理员审核。</p></header>

    <section class="project-workflow">
      <header><h2><ClipboardCheck :size="18" />客户项目分析与分派</h2></header>
      <div class="project-grid">
        <nav class="project-list">
          <button v-for="project in projects" :key="project.id" :class="{ active: selectedProject?.id === project.id }" @click="selectedProjectId = project.id">
            <b>{{ project.title }}</b><small>{{ project.client_name }} · {{ project.lifecycle_state }}</small>
          </button>
          <p v-if="!projects.length">暂无待处理项目</p>
        </nav>
        <main v-if="selectedProject" class="project-detail">
          <header><div><span>{{ selectedProject.client_name }}</span><h3>{{ selectedProject.title }}</h3></div><strong>{{ selectedProject.lifecycle_state }}</strong></header>
          <p class="plan">{{ selectedProject.solution?.teamContent?.fullPlan || selectedProject.solution?.teamContent?.summary }}</p>
          <article v-for="task in selectedProject.tasks" :key="task.id" class="assignment">
            <div><b>{{ task.title }}</b><p>{{ task.scope }}</p><small>当前负责人：{{ task.owner_name || "未分派" }}</small></div>
            <div class="assignment-controls">
              <button class="btn" @click="loadSuggestions(task.id)"><Bot :size="15" />能力推荐</button>
              <select v-model="selectedAssignees[task.id]" class="select">
                <option :value="null">选择员工</option>
                <option v-for="member in suggestions[task.id] || staff" :key="member.id" :value="member.id">
                  {{ member.display_name }}<template v-if="member.score !== undefined"> · {{ member.score }} 分</template>
                </option>
              </select>
              <button class="btn btn--primary" :disabled="!selectedAssignees[task.id] || loading" @click="assignTask(task)"><UserRoundCheck :size="15" />分派并 @员工</button>
            </div>
          </article>
          <textarea v-model="reviewNote" class="textarea" placeholder="管理员调整说明、客户需确认的问题或退回原因" />
          <div class="triage-actions">
            <button class="btn btn--primary" :disabled="loading" @click="triage('assign')"><Send :size="15" />确认进入执行</button>
            <button class="btn" :disabled="loading" @click="triage('client_review')"><ShieldCheck :size="15" />提交客户确认</button>
            <button class="btn" :disabled="loading" @click="triage('return')"><X :size="15" />退回客户补充</button>
          </div>
        </main>
      </div>
    </section>

    <div class="layout">
      <section><h2>员工能力与负载</h2><article v-for="member in staff" :key="member.id" class="member"><header><b>{{ member.display_name }}</b><span>{{ member.username }}</span></header><label>能力与等级（能力:1-5，逗号分隔）<textarea v-model="member.capabilityText" class="textarea" placeholder="VI设计:5, 视频剪辑:4, 文案:3" /></label><div class="row"><label>容量<input v-model.number="member.availability.capacity" class="input" type="number" /></label><label>当前负载<input v-model.number="member.availability.current_load" class="input" type="number" /></label><button class="btn" @click="save(member)"><Save :size="15" />保存</button></div></article></section>
      <section><h2><ShieldCheck :size="18" />待管理员审核</h2><article v-for="item in approvals" :key="item.id" class="approval"><header><b>{{ item.request_type === 'delivery' ? '员工交付' : '内部优化提案' }}</b><span :class="item.status">{{ item.status }}</span></header><p>{{ item.brief_title || item.payload?.title }}<br />{{ item.task_title || item.payload?.description }}</p><small>{{ item.submitter_name }} · {{ item.created_at.slice(0, 16).replace('T', ' ') }}</small><div v-if="item.status === 'pending_admin'" class="actions"><button class="btn btn--primary" @click="run(() => v3.decideApproval(item.id, 'approve', '管理员审核通过'))"><Check :size="15" />批准并提交客户</button><button class="btn" @click="run(() => v3.decideApproval(item.id, 'reject', '请按管理员意见修改后重提'))"><X :size="15" />退回员工</button></div></article><p v-if="!approvals.length">暂无审核事项</p></section>
      <aside><h2><Bot :size="18" />管理员 AI 助手</h2><textarea v-model="analysisInput" class="textarea" placeholder="粘贴客户需求、任务或方案，让 AI 分析风险、缺失信息和所需能力" /><button class="btn btn--primary" :disabled="loading" @click="analyze"><Bot :size="15" />分析</button><div v-if="analysis" class="analysis"><b>{{ analysis.summary }}</b><p>建议：{{ analysis.nextAction }}</p><h3>风险</h3><ul><li v-for="item in analysis.risks" :key="item">{{ item }}</li></ul><h3>缺失</h3><ul><li v-for="item in analysis.missing" :key="item">{{ item }}</li></ul><h3>所需能力</h3><span v-for="item in analysis.capabilities" :key="item">{{ item }}</span></div></aside>
    </div>
  </div>
</template>

<style scoped>
.admin{min-height:calc(100vh - 64px);padding:24px 28px;background:#f5f7f7}.admin>header h1{margin:0}.admin>header p{margin:4px 0 18px;color:var(--muted)}h2{display:flex;align-items:center;gap:7px;margin:0 0 12px;font-size:16px}.project-workflow{margin-bottom:16px;padding:18px;background:#fff;border:1px solid var(--line)}.project-grid{display:grid;grid-template-columns:260px minmax(0,1fr);gap:18px}.project-list{display:grid;align-content:start;border-right:1px solid var(--line)}.project-list button{display:grid;gap:3px;border:0;border-bottom:1px solid var(--line);padding:11px;background:transparent;text-align:left}.project-list button.active{border-left:3px solid var(--brand);background:#f6f8f8}.project-list small,.project-detail span,.assignment small{color:var(--muted)}.project-detail>header,.assignment{display:flex;align-items:flex-start;justify-content:space-between;gap:14px}.project-detail h3{margin:2px 0}.project-detail>header strong{color:var(--brand-dark);font-size:12px}.plan{max-height:150px;overflow:auto;white-space:pre-wrap;color:var(--muted);line-height:1.6}.assignment{padding:12px 0;border-top:1px solid var(--line)}.assignment>div:first-child{min-width:0}.assignment p{margin:3px 0;color:var(--muted)}.assignment-controls{display:grid;grid-template-columns:auto minmax(160px,220px) auto;gap:7px;flex:0 0 auto}.assignment-controls .btn,.triage-actions .btn{display:flex;align-items:center;justify-content:center;gap:6px}.project-detail>.textarea{min-height:72px;margin-top:10px}.triage-actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:8px}.layout{display:grid;grid-template-columns:minmax(320px,.8fr) minmax(340px,1fr) minmax(280px,.7fr);gap:16px}.layout>section,.layout>aside{align-self:start;padding:18px;background:#fff;border:1px solid var(--line)}.member,.approval{padding:12px 0;border-top:1px solid var(--line)}.member header,.approval header{display:flex;justify-content:space-between}.member header span,small{color:var(--muted)}label{display:grid;gap:4px;margin-top:8px;color:var(--muted);font-size:12px}.member .textarea{min-height:55px}.row{display:grid;grid-template-columns:1fr 1fr auto;align-items:end;gap:7px}.row .btn,.actions .btn,.layout>aside>.btn{display:flex;align-items:center;justify-content:center;gap:6px}.approval p{color:var(--muted)}.approval header span{font-size:11px}.approval header span.pending_admin{color:#b5661d}.actions{display:flex;gap:7px;margin-top:9px}.layout>aside>.btn{width:100%;margin-top:8px}.analysis{margin-top:14px;padding-top:12px;border-top:1px solid var(--line)}.analysis h3{margin:10px 0 3px;font-size:13px}.analysis ul{margin:0;padding-left:18px}.analysis span{display:inline-block;margin:2px;padding:3px 6px;background:#eef2f2}
@media(max-width:1100px){.layout{grid-template-columns:1fr 1fr}.layout>aside{grid-column:1/-1}.assignment{display:grid}.assignment-controls{grid-template-columns:auto minmax(160px,1fr) auto}}
@media(max-width:700px){.admin{padding:16px 12px}.project-grid,.layout{grid-template-columns:1fr}.project-list{display:flex;overflow-x:auto;border-right:0;border-bottom:1px solid var(--line)}.project-list button{flex:0 0 210px}.layout>aside{grid-column:auto}.row{grid-template-columns:1fr 1fr}.row .btn{grid-column:1/-1}.assignment-controls{grid-template-columns:1fr}.triage-actions{display:grid}.triage-actions .btn{width:100%}}
</style>
